import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import ConfirmDialog from "../modals/ConfirmDialog";

interface ConfirmOptions {
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

interface ConfirmDialogContextType {
  confirm: (message: string, options?: ConfirmOptions) => Promise<boolean>;
}

const ConfirmDialogContext = createContext<
  ConfirmDialogContextType | undefined
>(undefined);

export const useConfirmDialog = () => {
  const ctx = useContext(ConfirmDialogContext);
  if (!ctx)
    throw new Error(
      "useConfirmDialog must be used within a ConfirmDialogProvider",
    );
  return ctx.confirm;
};

export const ConfirmDialogProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  type Request = {
    message: string;
    options: ConfirmOptions;
    resolve: (result: boolean) => void;
  };
  const pending = useRef<Request[]>([]);
  const [request, setRequest] = useState<Request | null>(null);

  const confirm = useCallback((msg: string, opts?: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      const next = { message: msg, options: opts || {}, resolve };
      pending.current.push(next);
      if (pending.current.length === 1) setRequest(next);
    });
  }, []);

  const settle = (result: boolean) => {
    const current = pending.current.shift();
    if (!current) return;
    setRequest(pending.current[0] ?? null);
    current.resolve(result);
  };
  useEffect(
    () => () => {
      for (const current of pending.current.splice(0)) current.resolve(false);
    },
    [],
  );

  return (
    <ConfirmDialogContext.Provider value={{ confirm }}>
      {children}
      <ConfirmDialog
        open={request !== null}
        title={request?.options.title}
        message={request?.message ?? ""}
        onCancel={() => settle(false)}
        onConfirm={() => settle(true)}
        confirmLabel={request?.options.confirmLabel}
        cancelLabel={request?.options.cancelLabel}
      />
    </ConfirmDialogContext.Provider>
  );
};
